/**
 * Profile Keyboard Shortcuts Hook
 *
 * Global keyboard shortcuts for profile management:
 * - Cmd/Ctrl + Shift + P: Navigate to profiles list
 * - Cmd/Ctrl + Shift + N: Create new profile
 * - Cmd/Ctrl + 1-9: Quick switch to profile by position
 */

import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate } from 'react-router-dom';
import { useProfiles, useProfileSwitch } from './useProfiles';
import { toast } from 'react-hot-toast';

export function useProfileKeyboardShortcuts() {
  const navigate = useNavigate();
  const { data: profiles } = useProfiles();
  const { mutateAsync: switchProfile } = useProfileSwitch();

  // Navigate to profiles list page
  useHotkeys('ctrl+shift+p, meta+shift+p', (e: KeyboardEvent) => {
    e.preventDefault();
    navigate('/profiles');
    toast.success('Opening profiles...', {
      icon: '⌨️',
      duration: 1500,
    });
  });

  // Create new profile
  useHotkeys('ctrl+shift+n, meta+shift+n', (e: KeyboardEvent) => {
    e.preventDefault();
    navigate('/app/company-profile/wizard');
    toast.success('Creating new profile...', {
      icon: '⌨️',
      duration: 1500,
    });
  });

  // Quick switch to profile 1-9
  for (let i = 1; i <= 9; i++) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useHotkeys(`ctrl+${i}, meta+${i}`, async (e: KeyboardEvent) => {
      e.preventDefault();

      if (!profiles || profiles.length < i) {
        toast.error(`Profile ${i} not found`, {
          icon: '❌',
          duration: 2000,
        });
        return;
      }

      const targetProfile = profiles[i - 1];
      try {
        await switchProfile(targetProfile.id);
        toast.success(`Switched to ${targetProfile.profile_name}`, {
          icon: '⌨️',
          duration: 2000,
        });
      } catch (error: unknown) {
        toast.error((error as { message?: string })?.message || 'Failed to switch profile');
      }
    });
  }
}
